// Geocode address using Google Maps Geocoding API
import logger from './logger';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export async function geocodeAddress(address, city, state, zip) {
  // Build full address
  const fullAddress = [address, city, state, zip]
    .filter(Boolean)
    .join(', ')
    .trim();
  
  if (!fullAddress) {
    return { success: false, error: 'No address provided' };
  }

  if (!GOOGLE_MAPS_API_KEY) {
    logger.error('[Geocode] Google Maps API key not configured');
    return { success: false, error: 'API key not configured' };
  }

  const encodedAddress = encodeURIComponent(fullAddress);
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${GOOGLE_MAPS_API_KEY}`;

  try {
    logger.log('[Geocode] Requesting Google Maps API for:', fullAddress);
    
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Geocoding request failed');
    }

    const data = await response.json();
    logger.log('[Geocode] Google Maps API response status:', data.status);

    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      const formattedAddress = data.results[0].formatted_address;
      
      logger.log('[Geocode] Success:', formattedAddress, location);
      
      return {
        lat: location.lat,
        lng: location.lng,
        formattedAddress: formattedAddress,
        success: true
      };
    }

    if (data.status === 'ZERO_RESULTS') {
      // Try with just city and state as fallback
      const fallbackAddress = `${city}, ${state}`;
      const fallbackUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(fallbackAddress)}&key=${GOOGLE_MAPS_API_KEY}`;
      
      logger.log('[Geocode] Trying fallback:', fallbackAddress);
      
      const fallbackResponse = await fetch(fallbackUrl);
      const fallbackData = await fallbackResponse.json();

      if (fallbackData.status === 'OK' && fallbackData.results && fallbackData.results.length > 0) {
        const location = fallbackData.results[0].geometry.location;
        
        logger.log('[Geocode] Fallback success:', location);
        
        return {
          lat: location.lat,
          lng: location.lng,
          formattedAddress: fallbackData.results[0].formatted_address,
          success: true,
          fallback: true
        };
      }

      return { success: false, error: 'Address not found' };
    }

    if (data.status === 'REQUEST_DENIED') {
      logger.error('[Geocode] API key error:', data.error_message);
      return { success: false, error: 'API key invalid or Geocoding API not enabled' };
    }

    if (data.status === 'OVER_QUERY_LIMIT') {
      return { success: false, error: 'Geocoding quota exceeded' };
    }

    return { success: false, error: data.status || 'Unknown error' };
  } catch (error) {
    logger.error('[Geocode] Error:', error);
    return { success: false, error: error.message };
  }
}

// Re-geocode a single store (useful for updating existing stores)
export async function regeocodeStore(store) {
  const result = await geocodeAddress(
    store.address,
    store.city,
    store.state,
    store.zip
  );
  
  if (result.success) {
    return {
      ...store,
      lat: result.lat,
      lng: result.lng,
      geocodedAt: new Date().toISOString()
    };
  }
  
  return store;
}
